import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransactions } from "../../../../redux/slices/bookingSlice";
import classes from "./transactions.module.css";

const Transactions = () => {
  const dispatch = useDispatch();
  const { transactions } = useSelector((state) => state.login);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filter, setFilter] = useState({
    status: "all",
    type: "all",
    dateRange: "all",
  });

  useEffect(() => {
    dispatch(fetchTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (transactions?.results) {
      let filtered = [...transactions?.results];

      // Apply status filter
      if (filter.status !== "all") {
        filtered = filtered.filter(
          (transaction) => transaction.status === filter.status
        );
      }

      // Apply transaction type filter
      if (filter?.type !== "all") {
        filtered = filtered.filter(
          (transaction) => transaction.transactionRefType === filter.type
        );
      }

      // Apply date range filter
      if (filter.dateRange !== "all") {
        const today = new Date();
        let compareDate = new Date();

        switch (filter.dateRange) {
          case "today":
            compareDate.setHours(0, 0, 0, 0);
            break;
          case "week":
            compareDate.setDate(today.getDate() - 7);
            break;
          case "month":
            compareDate.setMonth(today.getMonth() - 1);
            break;
          default:
            break;
        }

        filtered = filtered.filter(
          (transaction) => new Date(transaction.createdAt) >= compareDate
        );
      }

      setFilteredTransactions(filtered);
    }
  }, [transactions, filter]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
    }).format(amount);
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case "booking_payment":
        return "Booking Payment";
      case "artist_settlement":
        return "Artist Settlement";
      case "token":
        return "Token";
      default:
        return type;
    }
  };

  return (
    <div className={classes.container}>
      <h1 className={classes.title}>Transaction History</h1>

      {/* Filters */}
      <div className={classes.filterContainer}>
        <div className={classes.filterGrid}>
          <div>
            <label className={classes.filterLabel}>Status</label>
            <select
              name="status"
              value={filter.status}
              onChange={handleFilterChange}
              className={classes.filterSelect}
            >
              <option value="all">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="initiated">Initiated</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className={classes.filterLabel}>Transaction Type</label>
            <select
              name="type"
              value={filter?.type}
              onChange={handleFilterChange}
              className={classes.filterSelect}
            >
              <option value="all">All Types</option>
              <option value="booking_payment">Booking Payment</option>
              <option value="artist_settlement">Artist Settlement</option>
              <option value="token">Token</option>
            </select>
          </div>

          <div>
            <label className={classes.filterLabel}>Date Range</label>
            <select
              name="dateRange"
              value={filter.dateRange}
              onChange={handleFilterChange}
              className={classes.filterSelect}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className={classes.tableContainer}>
        {filteredTransactions.length > 0 ? (
          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead className={classes.tableHeader}>
                <tr>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Date
                  </th>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Transaction ID
                  </th>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Type
                  </th>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Amount
                  </th>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Status
                  </th>
                  <th scope="col" className={classes.tableHeaderCell}>
                    Payment ID
                  </th>
                </tr>
              </thead>
              <tbody className={classes.tableBody}>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction._id} className={classes.tableRow}>
                    <td className={classes.tableCell}>
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td className={classes.tableCell}>
                      {transaction._id.substring(0, 8)}...
                    </td>
                    <td className={classes.tableCell}>
                      {getTransactionTypeLabel(transaction.transactionRefType)}
                    </td>
                    <td className={classes.tableCell}>
                      {formatAmount(transaction.amount, transaction.currency)}
                    </td>
                    <td className={classes.tableCell}>
                      <span
                        className={`${classes.statusBadge} ${
                          classes[transaction.status]
                        }`}
                      >
                        {transaction.status.charAt(0).toUpperCase() +
                          transaction.status.slice(1)}
                      </span>
                    </td>
                    <td className={classes.tableCell}>
                      {transaction.razorpay_payment_id
                        ? transaction.razorpay_payment_id.substring(0, 8) +
                          "..."
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={classes.noDataContainer}>
            <p className={classes.noDataText}>
              No transactions found for the selected filters.
            </p>
          </div>
        )}
      </div>

      {/* Transaction Summary */}
      <div className={classes.summaryContainer}>
        <div className={classes.summaryCard}>
          <h3 className={classes.summaryTitle}>Total Transactions</h3>
          <p className={classes.summaryValueTransactions}>
            {filteredTransactions.length}
          </p>
        </div>

        <div className={classes.summaryCard}>
          <h3 className={classes.summaryTitle}>Total Amount</h3>
          <p className={classes.summaryValueAmount}>
            {formatAmount(
              filteredTransactions.reduce(
                (sum, transaction) =>
                  sum +
                  (transaction.status === "paid" ? transaction.amount : 0),
                0
              ),
              "INR"
            )}
          </p>
        </div>

        <div className={classes.summaryCard}>
          <h3 className={classes.summaryTitle}>Pending Amount</h3>
          <p className={classes.summaryValuePending}>
            {formatAmount(
              filteredTransactions.reduce(
                (sum, transaction) =>
                  sum +
                  (transaction.status === "initiated" ? transaction.amount : 0),
                0
              ),
              "INR"
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
